import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Robot, PaperPlaneTilt, X } from '@phosphor-icons/react';
import api from '../lib/api';
import { useRealtime } from '../context/RealtimeContext';
import { useT } from '../i18n/I18nContext';

// Must match app/shared/constants.py CLAUDE_AUTHOR_ID on the backend.
const CLAUDE_AUTHOR_ID = 'claude-agent';

const initialsOf = (name) =>
  (name || '?')
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();

export default function ProjectChat({ projectId, users, currentUserId, canUseAi = true, onClose }) {
  const t = useT();
  const { subscribeProject } = useRealtime();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);
  const seenIds = useRef(new Set());
  const typingTimeout = useRef(null);

  const userMap = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  const append = useCallback((msg) => {
    if (!msg || seenIds.current.has(msg.id)) return;
    seenIds.current.add(msg.id);
    setMessages((prev) => [...prev, msg]);
  }, []);

  // Initial history.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    seenIds.current = new Set();
    api
      .get(`/projects/${projectId}/chat`, { params: { limit: 100 } })
      .then(({ data }) => {
        if (cancelled) return;
        data.forEach((m) => seenIds.current.add(m.id));
        setMessages(data);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [projectId]);

  // Live updates.
  useEffect(() => {
    const unsub = subscribeProject(projectId, (msg) => {
      if (msg.type === 'chat_message') {
        append(msg.payload);
        // A reply landing means the assistant is done thinking.
        if (msg.payload?.author_id === 'claude-agent') setTyping(false);
      } else if (msg.type === 'assistant_typing') {
        const on = !!msg.payload?.typing;
        setTyping(on);
        // Safety net: clear a stuck indicator if no reply/stop arrives.
        clearTimeout(typingTimeout.current);
        if (on) typingTimeout.current = setTimeout(() => setTyping(false), 60000);
      }
    });
    return () => { unsub(); clearTimeout(typingTimeout.current); };
  }, [projectId, subscribeProject, append]);

  // Keep pinned to the bottom on new messages.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading, typing]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setDraft('');
    try {
      const { data } = await api.post(`/projects/${projectId}/chat`, { body });
      append(data); // optimistic; the WS echo is de-duped by id
    } catch (err) {
      setDraft(body); // restore on failure
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      data-testid="project-chat"
      className="fixed inset-y-0 right-0 z-40 w-full max-w-md bg-white border-l border-zinc-200 shadow-xl flex flex-col"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Robot size={18} weight="fill" className="text-[#0055ff]" />
          <h2 className="font-display font-black tracking-tight text-lg">
            {t('chat.title') || 'Project chat'}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close') || 'Close'}
          className="p-1 text-zinc-400 hover:text-zinc-700 rounded-sm"
        >
          <X size={18} weight="bold" />
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
        {loading ? (
          <p className="text-sm text-zinc-400 text-center py-8">{t('common.loading') || 'Loading…'}</p>
        ) : messages.length === 0 ? (
          <div className="text-sm text-zinc-500 text-center py-8 space-y-1">
            <p className="font-medium">{t('chat.emptyTitle') || 'No messages yet'}</p>
            <p className="text-zinc-400">
              {t('chat.emptyHint') || 'Ask PM about anything on the board.'}
            </p>
          </div>
        ) : (
          messages.map((m) => (
            <ChatBubble
              key={m.id}
              message={m}
              author={userMap.get(m.author_id)}
              isOwn={m.author_id === currentUserId}
            />
          ))
        )}
        {typing && (
          <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="chat-typing">
            <span className="inline-flex gap-1" aria-hidden>
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 chat-typing-dot" />
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 chat-typing-dot" />
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 chat-typing-dot" />
            </span>
            {t('chat.typing') || 'PM is responding…'}
          </div>
        )}
      </div>

      <div className="border-t border-zinc-200 p-3 flex-shrink-0">
        {!canUseAi && (
          <p className="text-[11px] text-zinc-500 mb-2" data-testid="chat-no-ai">{t('chat.noAi')}</p>
        )}
        <div className="flex items-end gap-2">
          <textarea
            data-testid="chat-input"
            rows={6}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={canUseAi ? t('chat.placeholder') || 'Message PM…' : t('chat.placeholderNoAi')}
            className="flex-1 resize-none min-h-[160px] max-h-72 px-3 py-2 text-sm rounded-sm border border-zinc-200 bg-white focus:outline-none focus:border-zinc-400"
          />
          <button
            type="button"
            data-testid="chat-send"
            onClick={send}
            disabled={!draft.trim() || sending}
            className="h-9 w-9 flex items-center justify-center rounded-sm bg-zinc-900 text-white disabled:opacity-40 hover:bg-zinc-700"
            aria-label={t('chat.send') || 'Send'}
          >
            <PaperPlaneTilt size={16} weight="fill" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ message, author, isOwn }) {
  const isClaude = message.author_id === CLAUDE_AUTHOR_ID;
  const name = isClaude ? 'PM' : author?.name || author?.email || 'Unknown';

  if (isOwn) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-lg rounded-br-sm bg-zinc-900 text-white px-3 py-2 text-sm whitespace-pre-wrap break-words">
          {message.body}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      {isClaude ? (
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#0055ff] flex items-center justify-center text-white">
          <Robot size={15} weight="fill" />
        </span>
      ) : (
        <span
          style={author?.color ? { backgroundColor: author.color } : undefined}
          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-white ${
            author?.color ? '' : 'bg-zinc-500'
          }`}
        >
          {initialsOf(name)}
        </span>
      )}
      <div className="min-w-0 max-w-[80%]">
        <div className="text-[11px] font-medium text-zinc-500 mb-0.5">{name}</div>
        <div
          className={`rounded-lg rounded-tl-sm px-3 py-2 text-sm break-words ${
            isClaude ? 'bg-blue-50 text-zinc-800' : 'bg-zinc-100 text-zinc-800'
          }`}
        >
          {isClaude ? (
            <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.body}</ReactMarkdown>
            </div>
          ) : (
            <span className="whitespace-pre-wrap">{message.body}</span>
          )}
        </div>
      </div>
    </div>
  );
}
